import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, ScrollText, ClipboardList, GraduationCap, MessageCircle, Banknote,
  Flag, CheckCircle2, XCircle, MessageSquareOff, Trash2, Send, PauseCircle, PlayCircle, XOctagon, CalendarClock, PenLine,
} from "lucide-react";
import { useAuditLog } from "../../lib/adminQueues";

/**
 * AuditLogTab — an append-only trail of admin actions. Every admin
 * mutation (approve, reject, flag, verify, wall-dismiss, wall-remove,
 * disbursement-authorize) writes exactly one row here.
 *
 * Filter chips scope the query server-side. "Load more" paginates by
 * `before=<oldest.timestamp>`.
 */

const ACTION_META = {
  campaign_approve:  { label: "approved",  Icon: CheckCircle2,     tone: "grass",     verb: (n) => `approved ${n} campaign${n === 1 ? "" : "s"}` },
  campaign_reject:   { label: "returned",  Icon: XCircle,          tone: "scarlet",   verb: (n) => `returned ${n} campaign${n === 1 ? "" : "s"} with a note` },
  campaign_flag:     { label: "flagged",   Icon: Flag,             tone: "tangerine", verb: (n, meta) => `flagged ${n} campaign${n === 1 ? "" : "s"} for ${meta?.flag_type || ""} review` },
  student_verify:    { label: "verified",  Icon: CheckCircle2,     tone: "grass",     verb: (n) => `verified ${n} student${n === 1 ? "" : "s"}` },
  student_reject:    { label: "sent back", Icon: XCircle,          tone: "scarlet",   verb: (n) => `sent ${n} student${n === 1 ? "" : "s"} back for more info` },
  wall_dismiss:      { label: "dismissed", Icon: MessageSquareOff, tone: "cobalt",    verb: (n) => `dismissed ${n} wall flag${n === 1 ? "" : "s"}` },
  wall_remove:       { label: "removed",   Icon: Trash2,           tone: "scarlet",   verb: (n) => `removed ${n} message${n === 1 ? "" : "s"} from a wall` },
  disbursement_authorize: { label: "authorized", Icon: Send,       tone: "grass",     verb: (n) => `authorized ${n} grant${n === 1 ? "" : "s"} for transfer` },
  grant_create:      { label: "scheduled", Icon: Banknote,         tone: "cobalt",    verb: (n) => `scheduled ${n} grant${n === 1 ? "" : "s"}` },
  grant_cancel:      { label: "cancelled", Icon: XCircle,          tone: "scarlet",   verb: (n) => `cancelled ${n} scheduled grant${n === 1 ? "" : "s"}` },
  balance_topup:     { label: "topped up", Icon: Banknote,         tone: "grass",     verb: () => "moved funds from the bank into Stripe" },
  campaign_edit_approve: { label: "published", Icon: PenLine,      tone: "grass",     verb: (n) => `published edits to ${n} live page${n === 1 ? "" : "s"}` },
  campaign_edit_reject:  { label: "returned",  Icon: XCircle,      tone: "scarlet",   verb: (n) => `returned edits on ${n} live page${n === 1 ? "" : "s"} with a note` },
  campaign_pause:    { label: "paused",    Icon: PauseCircle,      tone: "tangerine", verb: (n) => `paused ${n} campaign${n === 1 ? "" : "s"}` },
  campaign_resume:   { label: "resumed",   Icon: PlayCircle,       tone: "grass",     verb: (n) => `resumed ${n} campaign${n === 1 ? "" : "s"}` },
  campaign_close:    { label: "closed",    Icon: XOctagon,         tone: "cobalt",    verb: (n, meta) => meta?.reason === "ended" ? `closed ${n} campaign${n === 1 ? "" : "s"} at end of term (nightly job)` : `closed ${n} campaign${n === 1 ? "" : "s"} early` },
  extension_approve: { label: "extended",  Icon: CalendarClock,    tone: "grass",     verb: (n, meta) => `extended ${n} campaign${n === 1 ? "" : "s"} by ${meta?.days_added || 15} days` },
  extension_reject:  { label: "declined",  Icon: XCircle,          tone: "scarlet",   verb: (n) => `declined ${n} extension request${n === 1 ? "" : "s"} with a note` },
  founding_edit:     { label: "edited",    Icon: PenLine,          tone: "cobalt",    verb: (n, meta) => `edited ${n} founding-donor entr${n === 1 ? "y" : "ies"}${meta?.fields?.length ? ` (${meta.fields.join(", ")})` : ""}` },
  founding_delete:   { label: "deleted",   Icon: Trash2,           tone: "scarlet",   verb: (n) => `deleted ${n} founding pledge${n === 1 ? "" : "s"}` },
  founding_self_edit: { label: "self-edited", Icon: PenLine,       tone: "tangerine", verb: (n, meta) => `updated their own wall entry${meta?.fields?.length ? ` (${meta.fields.join(", ")})` : ""}` },
  founding_edit_link_sent: { label: "link sent", Icon: Send,       tone: "cobalt",    verb: (n, meta) => `sent a private edit link${meta?.sent_to ? ` to ${meta.sent_to}` : ""}` },
};

const TONE_CLS = {
  grass:     "bg-grass/15 text-grass",
  scarlet:   "bg-scarlet/15 text-scarlet",
  tangerine: "bg-tangerine/15 text-tangerine",
  cobalt:    "bg-cobalt/15 text-cobalt",
};

const FILTERS = [
  { key: null,               label: "All",           Icon: ScrollText },
  { key: "campaign",         label: "Campaigns",     Icon: ClipboardList, param: "target_type" },
  { key: "student",          label: "Verifications", Icon: GraduationCap, param: "target_type" },
  { key: "wall_message",     label: "Moderation",    Icon: MessageCircle, param: "target_type" },
  { key: "disbursement",     label: "Disbursements", Icon: Banknote,      param: "target_type" },
];


const relTime = (iso) => {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const absTime = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const money = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const AuditLogTab = () => {
  const [filter, setFilter] = useState(null);
  const { items, loading, loadingMore, error, hasMore, refresh, loadMore } =
    useAuditLog({ targetType: filter });

  return (
    <div data-testid="tab-audit-log">
      {/* Filter chips */}
      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="audit-filters">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const Icon = f.Icon;
          return (
            <button
              key={f.label}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`audit-filter-${(f.key || "all")}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                active
                  ? "border-ink bg-ink text-sand"
                  : "border-border bg-bone text-ink hover:border-ink/40"
              }`}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              {f.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={refresh}
          data-testid="audit-refresh"
          className="ml-auto text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink"
        >
          Refresh
        </button>
      </div>

      {error && (
        <p className="mb-6 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">{error}</p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-olive">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading audit log…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="audit-empty"
          className="rounded-2xl border-2 border-dashed border-border bg-bone p-10 text-center"
        >
          <ScrollText className="mx-auto h-9 w-9 text-olive" strokeWidth={1.5} />
          <h3 className="mt-3 font-serif text-2xl text-ink">Nothing to show yet.</h3>
          <p className="mt-1 text-sm text-olive">
            Every admin action — approve, flag, verify, moderate, authorize — will appear here the moment you take it.
          </p>
        </div>
      ) : (
        <>
          <ol className="relative space-y-4 border-l-2 border-ink/8 pl-6" data-testid="audit-timeline">
            <AnimatePresence initial={false}>
              {items.map((row) => (
                <AuditRow key={row.audit_id} row={row} />
              ))}
            </AnimatePresence>
          </ol>

          {hasMore && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="audit-load-more"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bone px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-ink transition-colors hover:border-ink disabled:opacity-60"
              >
                {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Load older entries
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AuditRow = ({ row }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = ACTION_META[row.action];
  const count = row.target_ids?.length || 0;
  const summary = useMemo(
    () => (meta ? meta.verb(count, row.meta || {}) : ""),
    [meta, count, row.meta],
  );

  if (!meta) return null;

  const isSystem = (row.admin_user_id || "").startsWith("system:");
  const initials = isSystem ? "MRA" : (row.admin_email || "?").slice(0, 2).toUpperCase();
  const Icon = meta.Icon;
  const previewLabels = row.target_labels?.slice(0, 3) || [];

  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
      className="relative rounded-xl border border-border bg-bone p-5"
      data-testid={`audit-row-${row.audit_id}`}
    >
      {/* Node on the timeline rail */}
      <span
        aria-hidden
        className={`absolute -left-[34px] top-6 flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-sand ${TONE_CLS[meta.tone]}`}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
      </span>

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-serif text-lg text-ink" data-testid={`audit-summary-${row.audit_id}`}>
            <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[10px] font-bold text-sand">
              {initials}
            </span>
            <span className="text-olive">{isSystem ? "Nightly job" : (row.admin_email || "admin")}</span>{" "}
            <span className="text-ink">{summary}</span>
          </p>
          {previewLabels.length > 0 && (
            <p className="mt-1.5 text-sm text-olive" data-testid={`audit-preview-${row.audit_id}`}>
              {previewLabels.map((l, i) => (
                <span key={i}>
                  <span className="italic text-ink">{l}</span>
                  {i < previewLabels.length - 1 ? ", " : ""}
                </span>
              ))}
              {count > 3 && <span> … +{count - 3} more</span>}
            </p>
          )}
        </div>
        <span
          className="shrink-0 text-[11px] font-medium uppercase tracking-[0.18em] text-olive"
          title={absTime(row.timestamp)}
          data-testid={`audit-time-${row.audit_id}`}
        >
          {relTime(row.timestamp)}
        </span>
      </div>

      {/* Meta chips (reason quote / flag type / totals / skipped) */}
      {(row.meta?.reason || row.meta?.flag_type || row.meta?.total_cents != null || row.meta?.skipped) && (
        <div className="mt-3 space-y-2">
          {row.meta.flag_type && (
            <span className="inline-flex items-center gap-1 rounded-full bg-tangerine/12 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-tangerine">
              {row.meta.flag_type} review
            </span>
          )}
          {typeof row.meta.total_cents === "number" && (
            <span
              data-testid={`audit-total-${row.audit_id}`}
              className="ml-1 inline-flex items-center gap-1 rounded-full bg-grass/12 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-grass"
            >
              {money(row.meta.total_cents)} authorized
            </span>
          )}
          {row.meta.skipped ? (
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-scarlet/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-scarlet">
              {row.meta.skipped} skipped
            </span>
          ) : null}
          {row.meta.reason && (
            <blockquote
              data-testid={`audit-reason-${row.audit_id}`}
              className="rounded-md border-l-4 border-scarlet bg-scarlet/[0.04] px-4 py-2 font-serif text-sm italic text-ink"
            >
              &ldquo;{row.meta.reason}&rdquo;
            </blockquote>
          )}
        </div>
      )}

      {/* Expand for bulk actions with >3 targets */}
      {count > 3 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`audit-expand-${row.audit_id}`}
          className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink"
        >
          {expanded ? "Hide" : `Show all ${count} targets`}
        </button>
      )}
      <AnimatePresence>
        {expanded && count > 3 && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28 }}
            className="mt-2 space-y-1 overflow-hidden text-sm text-olive"
          >
            {row.target_labels.map((l, i) => (
              <li key={i} className="italic">{l}</li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </motion.li>
  );
};
